import cookies from 'react-cookies';
import { batch } from 'react-redux';
import cookieParser from 'cookie';
import App from 'next/app';

import { isTruthy } from '@r1-frontend/shared/guards';
import { getIsBot } from '@r1-frontend/shared/helpers/middleware/getIsBot';

import InitialCity from '~/src/app/city/InitialCity';
import {
    ABN_TEST_COOKIE,
    ABN_VARIANTS_COOKIE,
    PROVIDER_ID_COOKIE,
} from '~/src/constants';
import { getCookie } from '~/src/helpers/cookie';
import getRequester from '~/src/helpers/getRequester';
import { setCSRF } from '~/src/store/actionCreators/auth';
import { setMenuData } from '~/src/store/actionCreators/menu';
import { setAbnVariants, setCurrentCity, setProviderId } from '~/src/store/actions/city';
import selectProviderId from '~/src/store/selectors/selectProviderId';

import { wrapper } from './store/createStore';
/**
 * Attention:
 * getInitialAppProps has be called on the Client-Side and Server Side both
 *
 */
const baseInitialProps = wrapper.getInitialAppProps(store => async appContext => {
    // todo: Remove it after removing /v1/info/all from Server Side
    if (appContext.ctx.req) {
        const { req, res } = appContext.ctx;
        // @ts-ignore
        cookies.plugToRequest(req, res);
    }

    /**
     * We already have a rehydrated store on the client side,
     * so we will only receive data on the server
     */
    if (appContext.ctx.req && appContext.ctx.res) {
        const doRequest = getRequester(appContext.ctx.req);
        const dispatch = store.dispatch;
        const state = store.getState();

        /**
         * This is rewritten in https://gitlab.com/r1team/d2c/frontend/b2c-domru/-/blob/master/frontend/apps/b2c-domru/src/middlewares/withFinalRewrite.ts?ref_type=heads#L18,
         * so we have to read the city from query params.
         */
        const cityFromHost = appContext.ctx.query['city'];

        let providerId = selectProviderId(state);
        let cookieAbn = '';

        const userAgent = appContext.ctx.req.headers['user-agent'];
        const isBot = getIsBot(userAgent);

        const parsedCookie = cookieParser.parse(appContext.ctx.req.headers.cookie ?? '');
        const clientId = parsedCookie['_ga_cid'] || 'none';
        const abnTestValue = parsedCookie['abn_test'];

        // if providerId is already in store, we want to skip the next step.
        if (!providerId && cityFromHost) {
            const initiator = new InitialCity(appContext.ctx);
            const {
                providerId: nextProviderId,
                currentCity,
                abnVariants,
                menus,
            } = await initiator.getAll();

            cookieAbn = abnVariants;
            providerId = nextProviderId;

            batch(() => {
                dispatch(setProviderId(nextProviderId));
                dispatch(setCurrentCity(currentCity));
                dispatch(setAbnVariants(abnVariants));

                // todo: Store: Меню нужно перенести к текущему городу
                dispatch(setMenuData(menus));
            });
        }

        // TODO: Пробросить ip-шник в headers (при этом на песке не проверить)
        // TODO: Вызов тут по внутрикластерному должен идти
        const resp = await doRequest.b2cDomru<{ token: string }>({
            uri: '/api/csrf-token',
            method: 'GET',
        });
        if (resp.isSuccess) {
            dispatch(setCSRF(resp.payload.token));
        }

        if (isBot) {
            // оставляем логику докидывания в req кук запрошенных на сервере для ботов
            if (!parsedCookie[ABN_VARIANTS_COOKIE]) {
                appContext.ctx.req.headers.cookie += `; ${ABN_VARIANTS_COOKIE}=${cookieAbn}`;
            }
            if (!parsedCookie[PROVIDER_ID_COOKIE]) {
                appContext.ctx.req.headers.cookie += `; ${PROVIDER_ID_COOKIE}=${providerId}`;
            }

            console.log(JSON.stringify({
                event: 'InitialAppProps',
                message: 'BOT without reload: save cookie in req',
                fromUrl: appContext.ctx.req.url || 'none',
                fromReferer: appContext.ctx.req.headers.referer || 'none',
                providerId,
                userAgent,
            }));
        } else {
            /**
             * Check if required cookies are empty and reload, if it`s so,
             * to get them up from server (which set in middlewares and InitialProps)
             * for use by both the server from the request and the client from the browser
             */
            const notTruthy = [
                ABN_TEST_COOKIE,
                PROVIDER_ID_COOKIE,
                ABN_VARIANTS_COOKIE,
            ].filter((name) => !isTruthy(getCookie(name, appContext.ctx?.req)));

            if (notTruthy.length) {
                const { res, req } = appContext.ctx;

                console.log(JSON.stringify({
                    event: 'InitialAppProps',
                    message: 'USER reload: no-cookie',
                    fromUrl: req.url || 'none',
                    fromReferer: req.headers.referer || 'none',
                    userAgent,
                    clientId,
                    notTruthy,
                    abnTestPersonalId: abnTestValue,
                }));

                res.writeHead(302, { Location: req.url || '/' });
                res.end();
                return { pageProps: {} };
            } else {
                console.log(JSON.stringify({
                    event: 'InitialAppProps',
                    message: 'USER: has all cookie',
                    fromUrl: appContext.ctx.req.url || 'none',
                    fromReferer: appContext.ctx.req.headers.referer || 'none',
                    providerId,
                    userAgent,
                    clientId,
                    cookieAbn,
                    abnTestPersonalId: abnTestValue,
                }));
            }
        }
    }

    return {
        pageProps: {
            ...(await App.getInitialProps(appContext)).pageProps,
            store,
        },
    };
});

export default baseInitialProps;
